import React from "react";
import { useSelector } from "react-redux";

import { faArrowUpRightFromSquare, faCircleCheck, faPlus } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, Chip, Text } from "@performyard/py-components";

import type { GoalSuggestionState } from "../types";

import { getRequireGoalCategory } from "../../../selectors/settings";

import { formatTargetWithUnits } from "../utils";

import { useAskPerformYardStore } from "../AskPerformYardStore";

const formatDate = (dateStr: string): string => {
	const date = new Date(dateStr + "T00:00:00");
	return Number.isNaN(date.getTime())
		? dateStr
		: date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

type GoalPreviewCardProps = {
	suggestionState: GoalSuggestionState;
	onCreateGoal: () => void;
	isCreating?: boolean;
};

export const GoalPreviewCard: React.FC<GoalPreviewCardProps> = ({ suggestionState, onCreateGoal, isCreating }) => {
	const hasGoalCategories = useAskPerformYardStore((state) => state.hasGoalCategories);
	const requireCategory = useSelector(getRequireGoalCategory);
	const { suggestion, status } = suggestionState;

	if (status === "deleted") {
		return null;
	}

	const isCreated = status === "created";

	return (
		<div className="box-border flex max-w-full min-w-0 flex-col gap-2 rounded-md border border-solid border-neutral-3 bg-neutral-0 px-3 py-4">
			<div className="flex max-w-full min-w-0 items-start gap-1">
				<Text
					size="paragraph-3"
					weight="semi-bold"
					color="neutral-9"
					dangerouslySetClassName="min-w-0 flex-1 break-words"
				>
					{suggestion.name}
				</Text>
				{isCreated && (
					<span className="shrink-0">
						<Chip color="green" size={20} icon={faCircleCheck} label="Goal Created" />
					</span>
				)}
			</div>

			<div className="flex flex-col gap-1">
				<Text size="caption" color="neutral-9">
					Target: {formatTargetWithUnits(suggestion.target, suggestion.units)}
				</Text>
				<Text size="caption" color="neutral-9">
					Assignee: {suggestion.assigneeName}
				</Text>
				<Text size="caption" color="neutral-9">
					Start Date: {formatDate(suggestion.startDate)}
				</Text>
				<Text size="caption" color="neutral-9">
					Due Date: {formatDate(suggestion.endDate)}
				</Text>
				{(requireCategory || suggestion.categories.length > 0) && hasGoalCategories && (
					<Text size="caption" color="neutral-9">
						Category: {suggestion.categories.map((c) => c.name).join(", ")}
					</Text>
				)}
			</div>

			{isCreated ? (
				<ButtonStandard
					variant="outline"
					color="neutral"
					size="small"
					iconRight={faArrowUpRightFromSquare}
					onClick={() => {
						if (suggestionState.createdGoalId) {
							window.open(`/goal/${suggestionState.createdGoalId}/`, "_blank");
						}
					}}
				>
					View Goal
				</ButtonStandard>
			) : (
				// Goal creation isn't tethered to any page, so a restored card stays
				// actionable exactly like a live one — unlike coaching improvements.
				<ButtonStandard
					variant="outline"
					color="primary"
					size="small"
					iconLeft={faPlus}
					onClick={onCreateGoal}
					disabled={isCreating}
					data-testid="askpy-create-goal-button"
				>
					{isCreating ? "Creating..." : "Create Goal"}
				</ButtonStandard>
			)}
		</div>
	);
};

export default GoalPreviewCard;
